import React, { useEffect, useState } from 'react';
import { Alert, Form, Modal } from 'antd';

import RequirementFields, { marksOf, requirementsRebuildBody, requirementsRefusal } from '../packages/RequirementFields';
import { apiErrorMessage } from '../../utils/apiErrors';
import { publishingRefusal, rebuildPublisherPackage } from '../../services/publishing';

/** Words for the version a rebuild makes: a listed package keeps what it lists until a reviewer approves. */
function rebuildWords(pkg) {
	const next = (pkg.version || 1) + 1;
	return pkg.status === 'draft'
		? `It becomes version ${next}, read again from its collection with the choices it was made with.`
		: `Version ${next} is read again from its collection and sent for review. Discover keeps version ${pkg.version || 1} until a reviewer approves it, and collections that added it keep the version they got.`;
}

/**
 * Rebuild… on a publisher's package (keepr-api KPR-1331): the next version,
 * with the choices it was made with. A card or bundle also says which of its
 * cards can use a card you have (KPR-1816), reading the source ids from
 * `markedCards` (KPR-1876). Left alone, it posts what a rebuild always posted.
 *
 * `build.cards` is how many cards the build names, null for every card the
 * collection owns; which ones is withheld, so a count says so in the section.
 * `build.requirements` without `markedCards` is an API that cannot say which
 * cards are marked: no section, so a rebuild cannot replace what it cannot read.
 *
 * A refusal stays in the dialog, in the API's words. `onChanged` reads the page
 * again when another account changed the package, and `onDone(answer)` closes.
 */
export default function RebuildPackageModal({ pkg, open, onClose, onChanged, onDone }) {
	const [form] = Form.useForm();
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);
	const stored = pkg?.markedCards;
	const unknown = Boolean(pkg?.build?.requirements?.length) && !Array.isArray(stored);

	useEffect(() => {
		if (open) setError(null);
	}, [open]);

	useEffect(() => {
		if (open) form.setFieldsValue({ requirements: marksOf(stored) });
	}, [open, stored, form]);

	const submit = async () => {
		try { await form.validateFields(); } catch { return; } // The form says what is wrong.
		setBusy(true);
		setError(null);
		try {
			const body = unknown ? {} : requirementsRebuildBody(form.getFieldValue('requirements'), stored);
			const answer = await (Object.keys(body).length ? rebuildPublisherPackage(pkg._id, pkg.rev, body) : rebuildPublisherPackage(pkg._id, pkg.rev));
			onDone?.(answer);
		} catch (e) {
			setError(publishingRefusal(e) || requirementsRefusal(e) || apiErrorMessage(e, 'The package could not be rebuilt.'));
			if (e?.response?.data?.code === 'package_changed') onChanged?.();
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal open={open} title={pkg ? `Rebuild “${pkg.name}” from its collection` : 'Rebuild'} okText="Rebuild" onOk={submit} onCancel={onClose} okButtonProps={{ loading: busy }} destroyOnHidden>
			{pkg ? <p>{rebuildWords(pkg)}</p> : null}
			<Form form={form} layout="vertical" requiredMark={false}>
				{pkg && !unknown ? (
					<RequirementFields
						form={form}
						collectionId={pkg.sourceCollectionId}
						kind={pkg.kind}
						stored={stored}
						report={pkg.report}
						partial={Number.isInteger(pkg.build?.cards)}
						rebuild
					/>
				) : null}
			</Form>
			{error ? <Alert type="error" showIcon message={error} /> : null}
		</Modal>
	);
}
